import { useEffect, useState, useCallback } from 'react';
import { billingApi } from '../api/client';
import type { BillingEntitlements } from '../api/client';

type CapabilityKey = keyof BillingEntitlements['capabilities'];

const FREE_ENTITLEMENTS: BillingEntitlements = {
  plan: 'free',
  status: 'active',
  capabilities: {
    analytics: true,
    simulator: true,
    backtesting: true,
  },
  isTrialing: false,
  cancelAtPeriodEnd: false,
};

export interface UseEntitlementsResult {
  entitlements: BillingEntitlements;
  isLoading: boolean;
  /** True if the capability is enabled for the current plan */
  canUseFeature: (key: CapabilityKey) => boolean;
  refetch: () => void;
}

export function useEntitlements(): UseEntitlementsResult {
  const [entitlements, setEntitlements] = useState<BillingEntitlements>(FREE_ENTITLEMENTS);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(() => {
    setIsLoading(true);
    billingApi
      .getEntitlements()
      .then(setEntitlements)
      .catch(() => setEntitlements(FREE_ENTITLEMENTS))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const canUseFeature = useCallback(
    (key: CapabilityKey): boolean => entitlements.capabilities[key],
    [entitlements],
  );

  return { entitlements, isLoading, canUseFeature, refetch: fetch };
}
